// All team-specific long-form content for the four team pages.
// Layout and logic live in TeamPage.tsx — only text/data lives here.

export interface TeamSection {
  title: string;
  body: string;
}

export interface TeamPageContent {
  emoji: string;
  headline: string;
  tagline: string;
  joinButtonLabel: string;
  alreadyOnTeamToast: string;
  joinSuccessToast: string;
  color: string; // accent color for this team's page
  sections: TeamSection[];
}

export const TEAM_PAGE_CONTENT: Record<string, TeamPageContent> = {
  Griffins: {
    emoji: "🦁",
    headline: "The Griffins",
    tagline: "Brave enough to answer. Bold enough to be wrong out loud.",
    joinButtonLabel: "Join the Griffins",
    alreadyOnTeamToast:
      "You're already a Griffin — sit down, hero, we know.",
    joinSuccessToast: "Welcome to the Griffins! Try not to charge in without reading the question first.",
    color: "#4d0001",
    sections: [
      {
        title: "The Griffin State of Mind",
        body: `Griffins move through life with a level of confidence that suggests either impressive bravery or a complete misunderstanding of consequences. If there is a locked door, mysterious dark corridor, or a suspicious figure nearby, you are already halfway toward it - because deep down, you believe exciting things happen to people who say yes. Others may call this reckless, but you prefer the term “committed.”
        You believe actions reveal character more than planning ever could, which is why you are rarely accused of overthinking anything. You trust instinct, bravery, and the vague hope that things will work out - because they usually have so far.`,
      },
      {
        title: "The Fine Print",
        body: `You have a tendency to volunteer before understanding the assignment, leap before checking the landing, and confidently declare “I’ve got this” in situations where nobody (including you) can be sure of that. Even when things go wrong, they tend to go wrong in ways that become stories people repeat later—with admiration, confusion, or both.`,
      },
      {
        title: "Your Completely Minor Character Flaws",
        body: `Sometimes your confidence arrives several minutes before your common sense. You think “we’ll figure it out” is a complete plan. Griffins often think hesitation is something that happens to less interesting people. You believe consequences are mostly future-you problems. `,
      },
      {
        title: "Totally Normal Griffin Behavior",
        body: `You think most problems are solved by going directly at them.
        You have absolutely said “watch this” before a regrettable decision.
        You think near-disasters build character
        You consider yourself brave and your friends consider you stressful
        You secretly enjoy a heroic entrance`,
      },
    ],
  },

  Snakes: {
    emoji: "🐍",
    headline: "The Snakes",
    tagline: "They already knew the answer. They were just waiting for the right moment.",
    joinButtonLabel: "Join the Snakes",
    alreadyOnTeamToast:
      "You're already a Snake. Did you expect us to be surprised?",
    joinSuccessToast: "Welcome to the Snakes. We had a feeling about you.",
    color: "#1A472A",
    sections: [
      {
        title: "The Snake State of Mind",
        body: `You operate with a calm, deliberate awareness of how things fit together, often several steps ahead of everyone else in the room. You prefer influence over attention. Though, to be fair, both are nice. You don’t rush into situations—you arrive once you understand the structure, the incentives, and the likely outcomes of everyone involved.`,
      },
      {
        title: "The Fine Print",
        body: `You would call it preparation. Others might call it manipulation. You tend to notice patterns, incentives, and opportunities that other people are still casually walking past.
        There is a fine line between being strategic and treating everyday life like a chess match — and you are frequently standing directly on that line, admiring the view.`,
      },
      {
        title: "Your Completely Minor Character Flaws",
        body: `You treat conversations like negotiations even when they are not. You have a backup plan for your backup plan. You subtly test boundaries just to see where they are. You say “interesting…” when you absolutely mean: “I am storing this information”.`,
      },
      {
        title: "Totally Normal Snakes Behavior",
        body: `
        You quietly keep emotional receipts
        You believe compliments are investments
        You notice leverage in situations others don't even see
        You remember things people hoped you forgot
        You find loopholes without trying
        You are suspiciously good at negotiation and persuasion
        You somehow end up with slightly more than your fair share of the snacks
        You are calm in situations others find completely terrifying`,
      },
    ],
  },

  Badgers: {
    emoji: "🦡",
    headline: "The Badgers",
    tagline: "They showed up. Every time. That's actually most of it.",
    joinButtonLabel: "Join the Badgers",
    alreadyOnTeamToast:
      "You're already a Badger. Honestly, we're just glad you're here.",
    joinSuccessToast: "Welcome to the Badgers! We saved you a spot — we always do.",
    color: "#917b19",
    sections: [
      {
        title: "The Badger State of Mind",
        body: `You operate with a quiet, steady reliability that makes other people unconsciously relax around you. While other teams are dramatically charging into danger, plotting twelve moves ahead, or writing essays about it, Badgers are usually somewhere nearby growing magical plants, making snacks, and quietly assuming things will probably work out.`,
      },
      {
        title: "The Fine Print",
        body: `Badgers know loyalty matters more than status, naps are wildly underrated, and comfort is a perfectly respectable life goal. There’s a calm confidence here that people often underestimate. You’d rather protect your peace and avoid unnecessary nonsense — but if things fall apart, somehow everyone still ends up looking at you.`,
      },
      {
        title: "Your Completely Minor Character Flaws",
        body: `Of course, every magical personality comes with a few… quirks. Sometimes “being chill” becomes suspiciously close to avoiding responsibilities until tomorrow. Or next week. Or whenever the vibes improve. You are emotionally available at inconvenient scale. Sometimes you chill too hard. You know how important it is to relax and stay calm, but sometimes you take it too far and melt into the sofa.`,
      },
      {
        title: "Totally Normal Badgers Behavior",
        body: `
        You say “it’s fine” when it is, in fact, not fine
        People vent to you without asking permission
        You collect emotional baggage that isn’t yours
        You give advice that people actually follow
        You adopt chaotic friends and stabilize them
        You own at least one blanket that feels emotionally important
        You know an alarming amount about magical plants for someone “just vibing”`,
      },
    ],
  },

  Eagles: {
    emoji: "🦅",
    headline: "The Eagles",
    tagline: "Technically correct. The best kind of correct.",
    joinButtonLabel: "Join the Eagles",
    alreadyOnTeamToast:
      "You're already an Eagle. We know — we read all the books.",
    joinSuccessToast: "Welcome to the Eagles. Please cite your sources.",
    color: "#0e1a40",
    sections: [
      {
        title: "The Eagle State of Mind",
        body: `Your mind is always slightly ahead of where you are, wandering through questions that may or may not have been necessary to ask. You are driven by curiosity in a way that turns simple topics into sprawling mental maps of connections, side notes, and increasingly abstract follow-ups.
        You don’t just want answers—you want context, origin, implications, and three alternative interpretations.`,
      },
      {
        title: "The Fine Print",
        body: `Your mind is essentially a magical library that somehow contains both groundbreaking insight and a deeply unnecessary amount of trivia.
        You remember obscure details with terrifying accuracy. You are capable of turning a straightforward decision into a philosophical mess. You often don’t realize you’ve been thinking for an hour until you forget what the original question was.`,
      },
      {
        title: "Your Completely Minor Character Flaws",
        body: `You overthink compliments. You get lost in your own questions. You have strong opinions about things no one asked about. You struggle to stop optimizing already functional systems. You say “interesting” in a tone that makes people nervous. You collect useless facts like they are valuable currency. You believe every answer contains another question.`,
      },
      {
        title: "Totally Normal Eagles Behavior",
        body: `You fall into research spirals about trivial topics
        You think “I just have one quick question” and then absolutely do not
        You overanalyze jokes and then explain them to yourself
        You enjoy learning things with absolutely no practical purpose
        You mentally redesign Hogwarts curriculum
        You’ve accidentally made a fun fact feel aggressive`,
      },
    ],
  },
};